import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, ChevronRight } from 'lucide-react';
import { getHomepageNotices } from '@/services/noticeService';
import NoticeCard from '@/components/notices/NoticeCard';
import type { Notice } from '@/types/notices';
import { useLanguage } from '@/contexts/LanguageContext';

export default function HomepageNoticesSection() {
  const { tr } = useLanguage();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getHomepageNotices(4)
      .then(setNotices)
      .finally(() => setLoading(false));
  }, []);

  // Don't render section if no notices
  if (!loading && notices.length === 0) return null;

  return (
    <section className="section-spacing">
      <div className="container">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-semibold tracking-widest text-accent-strong uppercase mb-3 flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5" /> {tr('Updates', 'সর্বশেষ খবর')}
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">
              {tr('Travel Updates & Notices', 'যাত্রাসংক্রান্ত খবর ও বিজ্ঞপ্তি')}
            </h2>
          </motion.div>
          <Link
            to="/notices"
            className="text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
          >
            {tr('All notices', 'সব বিজ্ঞপ্তি')} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="glass-card p-5 animate-pulse">
                <div className="flex gap-2 mb-3">
                  <div className="h-4 w-16 bg-secondary rounded" />
                  <div className="h-4 w-12 bg-secondary rounded" />
                </div>
                <div className="h-5 bg-secondary rounded w-3/4 mb-2" />
                <div className="h-4 bg-secondary rounded w-full mb-1" />
                <div className="h-4 bg-secondary rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {notices.map((notice, i) => (
              <NoticeCard key={notice.id} notice={notice} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
